import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { CompanyService } from '../company.services';
import type { GroupType } from '../company.types';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { groupTypeSettingValidationSchema } from '../company.validations';
import type { InferType } from 'yup';
import { toast } from 'sonner';
import { useAuthStore } from '@/features/auth/auth.store';
import { useFormGuardStore } from '@/stores/form-guard.store';
import { getPageSize } from '@/stores/page-size.store';
import { useUrlFilters, listPathWithFilters } from '@/hooks/use-url-filters';

const LIST_ROUTE = '/setting/company/group-type-setting';

const useGroupTypeList = () => {
  /**
   * No `companyId` filter: `GET /group-type` only honours it for a super
   * Admin and otherwise scopes the list to the caller's own company, which is
   * exactly what this page wants.
   */
  const initialFilters: GroupType.IGroupTypeFilters = {
    search: '',
    page: 1,
    limit: getPageSize('setting.group-type'),
    sort: '',
    order: '',
    status: 'active',
  };

  const [filters, setFilters] =
    useUrlFilters<GroupType.IGroupTypeFilters>(initialFilters);

  const cleanFilters = useMemo(() => {
    const cleaned: Record<string, string | number> = {};

    // Only include non-empty values
    (
      Object.entries(filters) as [
        keyof GroupType.IGroupTypeFilters,
        string | number | undefined,
      ][]
    ).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });

    // Always include page and limit
    if (filters.page !== undefined) cleaned.page = filters.page;
    if (filters.limit !== undefined) cleaned.limit = filters.limit;

    return cleaned;
  }, [filters]);

  const { data, isLoading } = useQuery({
    queryKey: ['group-type-setting-list', cleanFilters],
    queryFn: CompanyService.getGroupTypeList,
  });

  const searchRef = useRef<HTMLInputElement | null>(null);

  const handleResetFilter = () => {
    if (searchRef.current) searchRef.current.value = '';
    setFilters(initialFilters);
  };

  return { filters, setFilters, data, isLoading, searchRef, handleResetFilter };
};

const useGroupTypeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const setIgnoreGuard = useFormGuardStore(state => state.setIgnoreGuard);
  const companyId = useAuthStore(state => state.user?.company?.id ?? '');

  const form = useForm({
    resolver: yupResolver(groupTypeSettingValidationSchema),
    defaultValues: {
      status: 'active',
      companyId: companyId,
    },
    mode: 'onChange',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['group-type-setting', id],
    queryFn: CompanyService.getGroupTypeById,
    enabled: !!id,
    select: data => data?.data,
  });

  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: CompanyService.createGroupType,
  });
  const editMutation = useMutation({
    mutationFn: CompanyService.editGroupType,
  });
  const deleteMutation = useMutation({
    mutationFn: CompanyService.editGroupType,
  });

  const onSubmit = (
    data: InferType<typeof groupTypeSettingValidationSchema>
  ) => {
    if (id) {
      editGroupType(data);
    } else {
      createGroupType(data);
    }
  };

  const createGroupType = (
    data: InferType<typeof groupTypeSettingValidationSchema>
  ) => {
    createMutation.mutate(data, {
      onSuccess: res => {
        setIgnoreGuard(true);
        queryClient.invalidateQueries();
        toast.success(res.meta.message);
        navigate(listPathWithFilters(LIST_ROUTE));
      },
      onError: err => toast.error(err.message),
    });
  };

  const editGroupType = (
    data: InferType<typeof groupTypeSettingValidationSchema>
  ) => {
    editMutation.mutate(
      { id: id ?? '', data },
      {
        onSuccess: res => {
          setIgnoreGuard(true);
          queryClient.invalidateQueries();
          toast.success(res.meta.message);
          navigate(listPathWithFilters(LIST_ROUTE));
        },
        onError: err => toast.error(err.message),
      }
    );
  };

  const handleReset = () => {
    form.reset({
      ...data,
      companyId: data?.companyId ?? companyId,
      status: data?.status ?? 'active',
    });
  };

  const handleDelete = () => {
    if (!id) return;

    const currentData = form.getValues();
    deleteMutation.mutate(
      {
        id,
        data: {
          ...currentData,
          status: 'suspended',
        },
      },
      {
        onSuccess: () => {
          setIgnoreGuard(true);
          queryClient.invalidateQueries();
          toast.success('Group type deleted successfully');
          navigate(listPathWithFilters(LIST_ROUTE));
        },
        onError: err => toast.error(err.message),
      }
    );
  };

  const handleBack = () => navigate(listPathWithFilters(LIST_ROUTE));

  useEffect(() => {
    if (data) {
      form.reset({
        ...data,
        companyId: data.companyId ?? companyId,
        status: data.status ?? 'active',
      });
    }
  }, [data, form, id, companyId]);

  return {
    id,
    form,
    isLoading,
    createMutation,
    editMutation,
    deleteMutation,
    handleBack,
    handleReset,
    handleDelete,
    onSubmit,
  };
};

export { useGroupTypeList, useGroupTypeForm };
